// TODO: This file is a work in progress probably be renamed and serve as the main file for all the filter types and definitions
import { useQuery } from '@tanstack/react-query';
import {
  DatePreset,
  FilterControlType,
  type DatePresetOption,
  type MultiSelectOption,
  type MultiSelectOptionGroup,
} from 'corso-admin-ui';
import {
  ClaimPaymentStatus,
  ClaimReasonEnum,
  ClaimResolutionMethodEnum,
  claimRiskLevel,
  CrewClaimResolutionMethodEnum,
  crewClaimResolutionMethodEnumName,
  CrewClaimRollupStatusCode,
  crewClaimRollupStatusCodeName,
  CrewMerchantUi,
  isTruthy,
  shippingClaimReasonName,
  StoreProductStatus,
  storeProductStatusName,
  TrackingStatusCode,
  trackingStatusCodeName,
} from 'corso-types';
import { useMemo } from 'react';
import { match } from 'ts-pattern';
import api from '~/api';
import { useProductGroups } from '~/features/settings/hooks/useProductGroups';
import { useClaimReasons } from '~/hooks/useClaimReasons';
import { useStoreClaimTags } from '~/hooks/useClaimTags';
import { useConfigSettings } from '~/hooks/useConfigSettings';
import { useStoreRegistrationTags } from '~/hooks/useRegistrationTags';
import { useStoreShippingClaimTags } from '~/hooks/useShippingClaimTags';
import { useStoreId } from '~/providers/StoreUserProvider';
import { shippingClaimResolutionMethodName } from '~/utils/enumNameMaps';

const useFilterProducts = () => {
  const storeId = useStoreId();

  return useQuery({
    queryKey: ['products', storeId],
    queryFn: () =>
      api.store(storeId).product.search({
        // OpenSearch returns variants, so this is a variant ceiling — far fewer products
        take: 2000,
        applyInventory: false,
      }),
  });
};

type KeysOfUnion<T> = T extends T ? keyof T : never;
type ViewEntity = CrewMerchantUi.SearchView['kind'];

type ViewForEntity<K extends ViewEntity> = Extract<
  CrewMerchantUi.SearchView,
  { kind: K }
>;

type ViewFilterId = KeysOfUnion<ViewForEntity<ViewEntity>['filters']>;
type ViewFilterIdForEntity<E extends ViewEntity> = KeysOfUnion<
  ViewForEntity<E>['filters']
>;

export { DatePreset, FilterControlType } from 'corso-admin-ui';

export type FilterTypeOptions = {
  [FilterControlType.text]: never;
  [FilterControlType.date]: DatePresetOption[];
  [FilterControlType.multiselect]: (
    | MultiSelectOption
    | MultiSelectOptionGroup
  )[];
  [FilterControlType.boolean]: [
    { label: string; value: true },
    { label: string; value: false },
  ];
};

const filterIdToControlType = {
  searchTerm: FilterControlType.text,
  claimReasons: FilterControlType.multiselect,
  claimReasonIds: FilterControlType.multiselect,
  claimReasonDetailIds: FilterControlType.multiselect,
  claimTaggedWith: FilterControlType.multiselect,
  registrationTaggedWith: FilterControlType.multiselect,
  shippingClaimTaggedWith: FilterControlType.multiselect,
  createdOn: FilterControlType.date,
  estimatedPurchaseDate: FilterControlType.date,
  hasBeenCanceled: FilterControlType.boolean,
  hasClaim: FilterControlType.boolean,
  hasError: FilterControlType.boolean,
  hasInspection: FilterControlType.boolean,
  hasUpsellExchange: FilterControlType.boolean,
  hasRegistration: FilterControlType.boolean,
  salesChannels: FilterControlType.multiselect,
  requestedResolutionMethods: FilterControlType.multiselect,
  resolutionMethods: FilterControlType.multiselect,
  shipmentStatus: FilterControlType.multiselect,
  source: FilterControlType.multiselect,
  statusCodes: FilterControlType.multiselect,
  hasShipment: FilterControlType.boolean,
  paymentStatuses: FilterControlType.multiselect,
  inspectionCreatedOn: FilterControlType.date,
  hasInstantExchange: FilterControlType.boolean,
  wasResolvedExternally: FilterControlType.boolean,
  hasShopNowItems: FilterControlType.boolean,
  riskLevel: FilterControlType.multiselect,
  isDenied: FilterControlType.boolean,
  productVariantIds: FilterControlType.multiselect,
  inspectionGrade: FilterControlType.multiselect,
  isOffPlatform: FilterControlType.boolean,
  productGroupIds: FilterControlType.multiselect,
  productStatuses: FilterControlType.multiselect,
  wasOutsideEligibilityWindow: FilterControlType.boolean,
} as const satisfies Record<ViewFilterId, FilterControlType>;
type FilterIdControlType = typeof filterIdToControlType;

type FilterValue<E extends ViewEntity, ID extends ViewFilterIdForEntity<E>> =
  ID extends ViewFilterIdForEntity<E> ? ViewForEntity<E>['filters'][ID] : never;
type FilterType<
  K extends ViewEntity,
  ID extends ViewFilterIdForEntity<K>,
> = ID extends keyof FilterIdControlType ? FilterIdControlType[ID] : never;

export type Filter<
  E extends ViewEntity = ViewEntity,
  ID extends ViewFilterIdForEntity<E> = ViewFilterIdForEntity<E>,
> = {
  [V in E]: {
    [K in ID]: {
      id: K;
      label: string;
      verb?: string;
      value?: FilterValue<V, K>;
      type: FilterType<V, K>;
    } & (FilterTypeOptions[FilterType<V, K>] extends never
      ? {
          options?: never;
        }
      : {
          options: FilterTypeOptions[FilterType<V, K>];
          placeholder?: string;
        });
  };
}[E][ID];

type Definition<E extends ViewEntity> = {
  kind: E;
  filters: { [ID in ViewFilterIdForEntity<E>]: Filter<E, ID> };
};

type ReturnFilterDefinition = Definition<'Return'>;

type WarrantyFilterDefinition = Definition<'Warranty'>;

type ShippingFilterDefinition = Definition<'Shipping'>;

type RegistrationFilterDefinition = Definition<'Registration'>;

type OrderFilterDefinition = Definition<'Order'>;

type ProductFilterDefinition = Definition<'Product'>;

export type FilterDefinition =
  | RegistrationFilterDefinition
  | ReturnFilterDefinition
  | ShippingFilterDefinition
  | WarrantyFilterDefinition
  | OrderFilterDefinition
  | ProductFilterDefinition;

const dateOptions = [
  { value: DatePreset.today, label: 'Today' },
  { value: DatePreset.last7Days, label: 'Last 7 Days' },
  { value: DatePreset.last30Days, label: 'Last 30 Days' },
  { value: DatePreset.last90Days, label: 'Last 90 Days' },
  { value: DatePreset.last12Months, label: 'Last 12 Months' },
  { value: DatePreset.custom, label: 'Custom' },
] as const satisfies FilterTypeOptions[typeof FilterControlType.date];

const createdOn = {
  id: 'createdOn',
  label: 'Date',
  verb: 'is',
  type: FilterControlType.date,
  options: dateOptions,
} satisfies Filter<ViewEntity, 'createdOn'>;

const inspectionCreatedOn = {
  id: 'inspectionCreatedOn',
  label: 'Inspection Date',
  verb: 'is',
  type: FilterControlType.date,
  options: dateOptions,
} satisfies Filter<'Return' | 'Warranty', 'inspectionCreatedOn'>;

const searchTerm = {
  id: 'searchTerm',
  label: 'Search Term',
  type: FilterControlType.text,
} satisfies Filter<ViewEntity, 'searchTerm'>;

const source = {
  id: 'source',
  label: 'Source',
  verb: 'is',
  type: FilterControlType.multiselect,
  options: [
    { value: 'Merchant_App', label: 'Merchant App' },
    { value: 'Customer_App', label: 'Customer App' },
  ],
} satisfies Filter<'Return' | 'Warranty', 'source'>;

const shipmentStatus = {
  id: 'shipmentStatus',
  label: 'Shipment Status',
  verb: 'is',
  type: FilterControlType.multiselect,
  options: Object.values(TrackingStatusCode).map((status) => ({
    value: status,
    label: trackingStatusCodeName[status],
  })),
} satisfies Filter<'Return' | 'Warranty', 'shipmentStatus'>;

const hasError = {
  id: 'hasError',
  label: 'Error',
  type: FilterControlType.boolean,
  options: [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ],
} satisfies Filter<'Return' | 'Warranty', 'hasError'>;

const hasInspection = {
  id: 'hasInspection',
  label: 'Inspection',
  type: FilterControlType.boolean,
  options: [
    {
      value: true,
      label: 'Yes',
    },
    {
      value: false,
      label: 'No',
    },
  ],
} satisfies Filter<'Return' | 'Warranty', 'hasInspection'>;

const hasShipment = {
  id: 'hasShipment',
  label: 'Shipment',
  type: FilterControlType.boolean,
  options: [
    {
      value: true,
      label: 'Yes',
    },
    {
      value: false,
      label: 'No',
    },
  ],
} satisfies Filter<'Return' | 'Warranty', 'hasShipment'>;

const statusCodes = {
  id: 'statusCodes',
  label: 'Status',
  verb: 'is',
  type: FilterControlType.multiselect,
  options: Object.values(CrewClaimRollupStatusCode).map((code) => ({
    value: code,
    label: crewClaimRollupStatusCodeName[code],
  })),
} satisfies Filter<'Return' | 'Warranty', 'statusCodes'>;

const paymentStatuses = {
  id: 'paymentStatuses',
  label: 'Payment Status',
  verb: 'is',
  type: FilterControlType.multiselect,
  options: Object.values(ClaimPaymentStatus).map((status) => ({
    value: status,
    label: status,
  })),
} as const;

const wasResolvedExternally = {
  id: 'wasResolvedExternally',
  label: 'Resolved Outside Corso',
  type: FilterControlType.boolean,
  options: [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ],
} satisfies Filter<'Return' | 'Warranty', 'wasResolvedExternally'>;

const riskLevel = {
  id: 'riskLevel',
  label: 'Risk Level',
  verb: 'is',
  type: FilterControlType.multiselect,
  options: Object.values(claimRiskLevel).map((level) => ({
    value: level,
    label: level,
  })),
} satisfies Filter<'Return' | 'Warranty', 'riskLevel'>;

const isDenied = {
  id: 'isDenied',
  label: 'Denied',
  type: FilterControlType.boolean,
  options: [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ],
} satisfies Filter<'Return' | 'Warranty', 'isDenied'>;

const productVariantGroups = (products: CrewMerchantUi.StoreProduct[]) => {
  // the group label is its key, so products sharing a title would collapse into one group
  const titleCount = new Map<string, number>();
  products.forEach((p) => {
    titleCount.set(p.title, (titleCount.get(p.title) ?? 0) + 1);
  });

  return products
    .map((p) => ({
      label:
        (titleCount.get(p.title) ?? 0) > 1
          ? `${p.title} (ID: ${p.idFromPlatform})`
          : p.title,
      keywords: [p.title, p.idFromPlatform],
      options: p.variants.map((v) => ({
        value: `${v.id}`,
        label: v.optionsValueString,
        keywords: [
          v.sku,
          v.titleWithOptions,
          v.barcode,
          v.idFromPlatform,
        ].filter(isTruthy),
      })),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const claimReasonOptions = (reasons: CrewMerchantUi.ClaimReason[]) =>
  reasons
    .map((reason) => ({
      value: `${reason.id}`,
      label: reason.name,
      keywords: [reason.name, reason.category.name],
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const claimReasonDetailOptions = (reasons: CrewMerchantUi.ClaimReason[]) =>
  reasons
    .flatMap((reason) =>
      reason.claimReasonDetails.map((detail) => ({
        value: `${detail.id}`,
        label: detail.name,
        keywords: [detail.name, reason.name],
      })),
    )
    .sort((a, b) => a.label.localeCompare(b.label));

const defineReturnFilters = (
  tags: string[],
  products: CrewMerchantUi.StoreProduct[],
  inspectionGrades: MultiSelectOption[],
  reasons: CrewMerchantUi.ClaimReason[],
) =>
  ({
    createdOn,
    searchTerm,
    source,
    statusCodes,
    shipmentStatus,
    riskLevel,
    hasError,
    hasShipment,
    requestedResolutionMethods: {
      id: 'requestedResolutionMethods',
      label: 'Resolution Method',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: Object.values(CrewClaimResolutionMethodEnum)
        .filter(
          (method) =>
            method !== CrewClaimResolutionMethodEnum.repair &&
            method !== CrewClaimResolutionMethodEnum.warrantyReview &&
            method !== CrewClaimResolutionMethodEnum.other,
        )
        .map((method) => ({
          value: method,
          label: crewClaimResolutionMethodEnumName[method],
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    claimTaggedWith: {
      id: 'claimTaggedWith',
      label: 'Tag',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: tags.map((tag) => ({ value: tag, label: tag })),
    },
    inspectionGrade: {
      id: 'inspectionGrade',
      label: 'Inspection Grade',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: inspectionGrades,
    },
    productVariantIds: {
      id: 'productVariantIds',
      label: 'Product',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: productVariantGroups(products),
      placeholder: 'Search by SKU, title or ID',
    },
    claimReasonIds: {
      id: 'claimReasonIds',
      label: 'Reason',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: claimReasonOptions(reasons),
      placeholder: 'Search by reason',
    },
    claimReasonDetailIds: {
      id: 'claimReasonDetailIds',
      label: 'Reason Detail',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: claimReasonDetailOptions(reasons),
      placeholder: 'Search by reason detail',
    },
    hasInspection,
    hasInstantExchange: {
      id: 'hasInstantExchange',
      label: 'Instant Exchange',
      type: FilterControlType.boolean,
      options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
    },
    hasUpsellExchange: {
      id: 'hasUpsellExchange',
      label: 'Upsell Exchange',
      type: FilterControlType.boolean,
      options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
    },
    paymentStatuses,
    inspectionCreatedOn,
    wasResolvedExternally,
    isDenied,
    hasShopNowItems: {
      id: 'hasShopNowItems',
      label: 'Shop Now',
      type: FilterControlType.boolean,
      options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
    },
    wasOutsideEligibilityWindow: {
      id: 'wasOutsideEligibilityWindow',
      label: 'Outside Eligibility Window',
      type: FilterControlType.boolean,
      options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
    },
  }) satisfies ReturnFilterDefinition['filters'];

const defineWarrantyFilters = (
  tags: string[],
  salesChannels: string[],
  products: CrewMerchantUi.StoreProduct[],
  inspectionGrades: MultiSelectOption[],
  reasons: CrewMerchantUi.ClaimReason[],
) =>
  ({
    createdOn,
    searchTerm,
    source,
    statusCodes,
    shipmentStatus,
    hasError,
    wasResolvedExternally,
    isDenied,
    salesChannels: {
      id: 'salesChannels',
      verb: 'is',
      label: 'Registration Channel',
      type: FilterControlType.multiselect,
      options: salesChannels.map((channel) => ({
        value: channel,
        label: channel,
      })),
    },
    hasRegistration: {
      id: 'hasRegistration',
      label: 'Registration',
      type: FilterControlType.boolean,
      options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
    },
    hasShipment,
    hasInspection,
    riskLevel,
    requestedResolutionMethods: {
      id: 'requestedResolutionMethods',
      label: 'Resolution Method',
      type: FilterControlType.multiselect,
      verb: 'is',
      options: Object.values(CrewClaimResolutionMethodEnum)
        .map((method) => ({
          value: method,
          label: crewClaimResolutionMethodEnumName[method],
        }))
        .filter(
          (method) => method.value !== CrewClaimResolutionMethodEnum.other,
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    claimTaggedWith: {
      id: 'claimTaggedWith',
      label: 'Tag ',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: tags.map((tag) => ({ value: tag, label: tag })),
    },
    productVariantIds: {
      id: 'productVariantIds',
      label: 'Product',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: productVariantGroups(products),
      placeholder: 'Search by SKU, title or ID',
    },
    claimReasonIds: {
      id: 'claimReasonIds',
      label: 'Reason',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: claimReasonOptions(reasons),
      placeholder: 'Search by reason',
    },
    claimReasonDetailIds: {
      id: 'claimReasonDetailIds',
      label: 'Reason Detail',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: claimReasonDetailOptions(reasons),
      placeholder: 'Search by reason detail',
    },
    paymentStatuses,
    inspectionCreatedOn,
    inspectionGrade: {
      id: 'inspectionGrade',
      label: 'Inspection Grade',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: inspectionGrades,
    },
    wasOutsideEligibilityWindow: {
      id: 'wasOutsideEligibilityWindow',
      label: 'Outside Eligibility Window',
      type: FilterControlType.boolean,
      options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
    },
  }) satisfies WarrantyFilterDefinition['filters'];

const defineShippingFilters = (
  offeredClaimReasons: `${ClaimReasonEnum}`[],
  shippingClaimTags: string[],
) =>
  ({
    createdOn,
    searchTerm,
    claimReasons: {
      id: 'claimReasons',
      label: 'Reason',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: offeredClaimReasons.map((offeredReason) => ({
        value: offeredReason,
        label: shippingClaimReasonName[offeredReason],
      })),
    },
    resolutionMethods: {
      id: 'resolutionMethods',
      label: 'Resolution',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: Object.values(ClaimResolutionMethodEnum).map((value) => ({
        value,
        label: shippingClaimResolutionMethodName[value],
      })),
    },
    shippingClaimTaggedWith: {
      id: 'shippingClaimTaggedWith',
      label: 'Tag',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: shippingClaimTags.map((tag) => ({ value: tag, label: tag })),
    },
  }) satisfies ShippingFilterDefinition['filters'];

const defineRegistrationFilters = (
  salesChannels: string[],
  registrationTags: string[],
  products: CrewMerchantUi.StoreProduct[],
) =>
  ({
    createdOn,
    searchTerm,
    hasClaim: {
      id: 'hasClaim',
      label: 'Warranty Claim',
      type: FilterControlType.boolean,
      options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
    },
    productVariantIds: {
      id: 'productVariantIds',
      label: 'Product',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: productVariantGroups(products),
    },
    hasBeenCanceled: {
      id: 'hasBeenCanceled',
      label: 'Canceled',
      type: FilterControlType.boolean,
      options: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
    },
    estimatedPurchaseDate: {
      id: 'estimatedPurchaseDate',
      label: 'Purchase Date',
      verb: 'is',
      type: FilterControlType.date,
      options: dateOptions,
    },
    salesChannels: {
      id: 'salesChannels',
      label: 'Channel',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: salesChannels.map((channel) => ({
        value: channel,
        label: channel,
      })),
    },
    registrationTaggedWith: {
      id: 'registrationTaggedWith',
      label: 'Tags',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: registrationTags.map((tag) => ({ value: tag, label: tag })),
    },
  }) satisfies RegistrationFilterDefinition['filters'];

const defineOrderFilters = () =>
  ({
    createdOn,
    searchTerm,
  }) satisfies OrderFilterDefinition['filters'];

const defineProductFilters = (
  productGroups: CrewMerchantUi.StoreProductGroup[],
) =>
  ({
    createdOn,
    searchTerm,
    isOffPlatform: {
      id: 'isOffPlatform',
      label: 'Source',
      verb: 'is',
      type: FilterControlType.boolean,
      options: [
        { value: true, label: 'Corso' },
        { value: false, label: 'Shopify' },
      ],
    },
    productGroupIds: {
      id: 'productGroupIds',
      label: 'Product Group',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: productGroups
        .map((group) => ({ value: `${group.id}`, label: group.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    productStatuses: {
      id: 'productStatuses',
      label: 'Status',
      verb: 'is',
      type: FilterControlType.multiselect,
      options: Object.values(StoreProductStatus).map((status) => ({
        value: status,
        label: storeProductStatusName[status],
      })),
    },
  }) satisfies ProductFilterDefinition['filters'];

export function useFilterDefinition<Entity extends ViewEntity>(entity: Entity) {
  const { data: tags = [] } = useStoreClaimTags((claimTags) =>
    claimTags.map((tag) => tag.name),
  );
  const { data: registrationTags = [] } = useStoreRegistrationTags((regTags) =>
    regTags.map((tag) => tag.name),
  );
  const { data: shippingClaimTags = [] } = useStoreShippingClaimTags((spTags) =>
    spTags.map((tag) => tag.name),
  );

  const { data: registrationSettings } = useConfigSettings(
    ({ registrations }) => registrations,
  );

  const { data: offeredClaimReasons } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection.offeredClaimReasons,
  );

  const { data: inspectionGradesOptions } = useConfigSettings(
    ({ inspectionGrades }) =>
      inspectionGrades.map((grade) => ({
        value: grade.code,
        label: grade.name,
      })),
  );

  const { data: products = [] } = useFilterProducts();

  const { data: reasons = [] } = useClaimReasons();

  const { data: productGroups } = useProductGroups();

  return useMemo(
    () =>
      match<ViewEntity>(entity)
        .returnType<FilterDefinition>()
        .with('Return', (kind) => ({
          kind,
          filters: defineReturnFilters(
            tags,
            products,
            inspectionGradesOptions ?? [],
            reasons,
          ),
        }))
        .with('Warranty', (kind) => ({
          kind,
          filters: defineWarrantyFilters(
            tags,
            registrationSettings?.salesChannelOptions.map((c) => c.name) ?? [],
            products,
            inspectionGradesOptions ?? [],
            reasons,
          ),
        }))
        .with('Shipping', (kind) => ({
          kind,
          filters: defineShippingFilters(
            offeredClaimReasons ?? [],
            shippingClaimTags,
          ),
        }))
        .with('Registration', (kind) => ({
          kind,
          filters: defineRegistrationFilters(
            registrationSettings?.salesChannelOptions.map((c) => c.name) ?? [],
            registrationTags,
            products,
          ),
        }))
        .with('Order', (kind) => ({
          kind,
          filters: defineOrderFilters(),
        }))
        .with('Product', (kind) => ({
          kind,
          filters: defineProductFilters(productGroups),
        }))
        .exhaustive(),
    [
      tags,
      registrationTags,
      shippingClaimTags,
      entity,
      offeredClaimReasons,
      registrationSettings,
      products,
      inspectionGradesOptions,
      reasons,
      productGroups,
    ],
  );
}
